import { Fragment, useEffect, useRef, useState } from 'react';
import { Size } from '@optimus-web/common';
import { TagVariant } from '@optimus-web/core';
import { ActivableRenderer, ActivableRendererProps, Portal } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { IconButton } from '../IconButton';
import { Tag } from '../Tag';
import {
    SidePanelAction,
    SidePanelContainer,
    SidePanelContent,
    SidePanelHeader,
    SidePanelHeaderAction,
    SidePanelHeaderTitle,
    SidePanelWrapper,
} from './style';

interface SidePanelProps extends ActivableRendererProps {
    onClose: () => void;
    title: React.ReactNode;
    tag?: { text: string; variant: TagVariant };
    content: React.ReactNode;
    actions?: React.ReactNode;
    opened: boolean;
    portalContainerId?: string;
    headerActions: React.ReactNode;
    width: Size;
    /**
     * Workaround for react hook form content. Since Portal is used here actual position
     * of form fields is not inside of <form> (and <FormProvider/>) element which is used to wrap
     * <SidePanel /> component.
     */
    Wrapper?: React.FC;
}

const SidePanelActivatorFunction = ActivableRenderer<React.Component<SidePanelProps>, SidePanelProps>({
    activePropName: 'opened',
});

const SidePanelComponent = ({
    onClose,
    title,
    tag,
    content,
    actions,
    opened,
    portalContainerId,
    headerActions,
    width,
    Wrapper = Fragment,
    ...other
}: SidePanelProps) => {
    const [hasScrollBar, setHasScrollBar] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = contentRef?.current;

        if (!element) return;

        const setScrollBarState = () => setHasScrollBar(element.clientHeight < element.scrollHeight);

        if ('ResizeObserver' in window) {
            const observer = new ResizeObserver(() => {
                setScrollBarState();
            });

            observer.observe(element);
        } else {
            setScrollBarState();
        }
    }, []);

    return (
        <Portal containerId={portalContainerId} {...other}>
            <Wrapper>
                <SidePanelWrapper>
                    <SidePanelContainer opened={opened} width={width} data-test-sidepanel>
                        <SidePanelHeader>
                            <IconButton variant="tertiary" size="medium" icon={IconName.CrossClose} onClick={onClose} />
                            <SidePanelHeaderTitle>
                                {title}
                                {tag ? <Tag variant={tag.variant}>{tag.text}</Tag> : null}
                            </SidePanelHeaderTitle>
                            <SidePanelHeaderAction>{headerActions}</SidePanelHeaderAction>
                        </SidePanelHeader>
                        <SidePanelContent ref={contentRef}>{content}</SidePanelContent>
                        {actions ? <SidePanelAction hasScrollBar={hasScrollBar}>{actions}</SidePanelAction> : null}
                    </SidePanelContainer>
                </SidePanelWrapper>
            </Wrapper>
        </Portal>
    );
};

export const SidePanel = SidePanelActivatorFunction(SidePanelComponent);
